import React, { useEffect, useState } from "react";
import "./StartingQuestionnaire.css";
import Button from "@mui/material/Button";
import { CONFIGS, DEBUG_MODE } from "../../config/appConfig";
import {
  DEBUG_PREFILL_PARTICIPANT_CODE,
  getDebugStartingQuestionnaireData,
  isDebugPrefillParticipantCode,
  normalizeParticipantCode,
} from "../../utils/debugPrefill";

/**
 * Starting questionnaire – demographics, ATI, English proficiency, TIPI.
 *
 * When Prolific params are provided (prolificPid, studyId, assignedMode),
 * step 1 is auto-populated and read-only. In debug/manual mode the fields
 * remain editable.
 *
 * @param {Object} props
 * @param {Function} props.handleFormSubmission
 * @param {string|null} props.prolificPid - From URL param PROLIFIC_PID
 * @param {string|null} props.studyId - From URL param STUDY_ID
 * @param {string|null} props.assignedMode - Mode assigned by backend counter-balance
 */
const TIPI_GLOSSARY = {
  Extraverted: "Outgoing and comfortable around other people.",
  enthusiastic: "Showing strong interest or enjoyment.",
  Critical: "Often noticing problems or judging things carefully.",
  quarrelsome: "Likely to argue or disagree with others.",
  Dependable: "Reliable; someone others can count on.",
  "self-disciplined": "Able to stay organized and follow through on plans.",
  Anxious: "Often worried or nervous.",
  Reserved: "Quiet or private in social situations.",
  Sympathetic: "Able to understand and care about others' feelings.",
  Disorganised: "Not very orderly or planned.",
  Conventional: "Preferring familiar or traditional ways of doing things.",
  uncreative: "Less interested in original or imaginative ideas.",
  "Open to new experiences": "Curious and willing to try new things or ideas.",
  complex: "Interested in deep, complicated, or abstract ideas.",
};

const STEP_TITLES = {
  1: "Your Study Access",
  2: "A Few Questions About You",
  3: "Affinity For Technology",
  4: "English Language Experience",
  5: "How You See Yourself",
};

function WordInfo({ word, definition }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  return (
    <span
      className="word-info-wrap"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => {
        if (!pinned) {
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        className="word-info"
        aria-label={`${word}: ${definition}`}
        aria-expanded={open}
        onClick={() => {
          setOpen((prev) => !prev || !pinned);
          setPinned((prev) => !prev);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          setPinned(false);
        }}
      >
        i
      </button>
      {open && (
        <span className="word-tooltip" role="tooltip">
          {definition}
        </span>
      )}
    </span>
  );
}

const tooltipFor = (word) => {
  const definition = TIPI_GLOSSARY[word];
  if (!definition) return null;
  return <WordInfo word={word} definition={definition} />;
};

const term = (word) => (
  <span className="question-word">
    {word}
    {tooltipFor(word)}
  </span>
);

function Questionnaire({
  handleFormSubmission,
  handleAccessComplete,
  prolificPid,
  studyId,
  assignedMode: assignedModeFromProlific,
  consentData,
  accessData,
  accessOnly = false,
  showDebugControls = false,
}) {
  // Prolific participants are identified by their URL params alone. The study
  // condition is assigned only after consent (so people who bounce or decline
  // do not consume a counter-balancing slot), so it may not be known yet here.
  const isProlific = !!(prolificPid && studyId);
  console.log(`[StartingQuestionnaire] props: prolificPid="${prolificPid}", studyId="${studyId}", assignedMode="${assignedModeFromProlific}", isProlific=${isProlific}`);

  const firstStep = accessData && !accessOnly ? 2 : 1;
  const [currentStep, setCurrentStep] = useState(accessOnly ? 1 : firstStep);
  const [validationError, setValidationError] = useState("");
  const [debugPrefillApplied, setDebugPrefillApplied] = useState(false);
  const [formData, setFormData] = useState({
    participantCode: accessData?.participantCode || prolificPid || "",
    assignedMode: accessData?.assignedMode || assignedModeFromProlific || "",
    consentAccepted: consentData?.consentAccepted || false,
    consentVersion: consentData?.consentVersion || "",
    consentTimestamp: consentData?.consentTimestamp || "",
    ageGroup: "",
    gender: "",
    profession: "",
    ati: {
      occupyDetail: "",
      testFunctions: "",
      dealBecauseMust: "",
      tryIntensively: "",
      enjoyAcquainting: "",
      enoughWorks: "",
      understandHow: "",
      enoughBasic: "",
      fullCapabilities: "",
    },
    englishProficiency: {
      listening: "",
      reading: "",
      spokenInteraction: "",
      spokenProduction: "",
      writing: "",
    },
    tipi: {
      extraverted: "",
      critical: "",
      dependable: "",
      anxious: "",
      openToExperiences: "",
      reserved: "",
      sympathetic: "",
      disorganised: "",
      calm: "",
      conventional: "",
    },
  });

  useEffect(() => {
    if (accessOnly) {
      return;
    }

    if (!isDebugPrefillParticipantCode(formData.participantCode) || debugPrefillApplied) {
      return;
    }

    setFormData((prev) =>
      ({
        ...getDebugStartingQuestionnaireData({
          participantCode: prev.participantCode,
          assignedMode: prev.assignedMode || assignedModeFromProlific,
        }),
        consentAccepted: prev.consentAccepted,
        consentVersion: prev.consentVersion,
        consentTimestamp: prev.consentTimestamp,
      })
    );
    setCurrentStep(5);
    setValidationError("");
    setDebugPrefillApplied(true);
  }, [accessOnly, assignedModeFromProlific, debugPrefillApplied, formData.participantCode]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      participantCode: accessData?.participantCode || prolificPid || prev.participantCode,
      assignedMode: accessData?.assignedMode || assignedModeFromProlific || prev.assignedMode,
      consentAccepted: consentData?.consentAccepted || false,
      consentVersion: consentData?.consentVersion || "",
      consentTimestamp: consentData?.consentTimestamp || "",
    }));
  }, [accessData, assignedModeFromProlific, consentData, prolificPid]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValidationError("");
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (parentField, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value,
      },
    }));
  };

  const validateCurrentStep = () => {
    const form = document.querySelector("form");
    if (!form) {
      return true;
    }

    // Age group and profession are required fields (HTML `required`), and the
    // age-group options start at 18, so browser validation covers this step.
    const isValid = form.reportValidity();
    if (isValid) {
      setValidationError("");
    }
    return isValid;
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setValidationError("");
      if (accessOnly) {
        handleAccessComplete?.({
          participantCode: formData.participantCode,
          assignedMode: formData.assignedMode,
        });
        return;
      }
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setValidationError("");
    setCurrentStep((prev) => Math.max(prev - 1, firstStep));
  };

  const handleSubmission = (e) => {
    e.preventDefault();
    setValidationError("");
    if (!validateCurrentStep()) {
      return;
    }
    if (accessOnly) {
      handleAccessComplete?.({
        participantCode: formData.participantCode,
        assignedMode: formData.assignedMode,
      });
      return;
    }
    console.log(`[StartingQuestionnaire] Submitting formData.assignedMode="${formData.assignedMode}", formData.participantCode="${formData.participantCode}"`);
    handleFormSubmission?.(formData);
  };

  const fillDebugAccess = () => {
    setValidationError("");
    setFormData((prev) => ({
      ...prev,
      participantCode: DEBUG_PREFILL_PARTICIPANT_CODE,
      assignedMode: CONFIGS.MODE_5,
    }));
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="page">
            <div className="section">
              {isProlific ? (
                <>
                  <p style={{ color: "#16a34a" }}>
                    Your participant ID and condition have been assigned automatically via Prolific.
                  </p>
                  <div className="form-group">
                    <label>Participant Code</label>
                    <input type="text" value={formData.participantCode} readOnly />
                  </div>
                  {formData.assignedMode ? (
                    <div className="form-group">
                      <label>Assigned Condition</label>
                      <input type="text" value={formData.assignedMode} readOnly />
                    </div>
                  ) : (
                    <p style={{ color: "#64748b", fontSize: 13 }}>
                      Your study condition will be assigned automatically once you
                      begin.
                    </p>
                  )}
                </>
              ) : (
                <>
                  {showDebugControls && accessOnly && (
                    <div className="debug-access-panel">
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={fillDebugAccess}
                        type="button"
                      >
                        Use debug participant
                      </Button>
                      <p>
                        Fills DEBUG002 with the complementary explanation condition for local checks.
                      </p>
                    </div>
                  )}

                  <div className="form-group">
                    <label>
                      1. Please enter the participant code provided by the researcher. *
                    </label>
                    <input
                      type="text"
                      value={formData.participantCode}
                      onChange={(e) => {
                        const value = normalizeParticipantCode(e.target.value);
                        setFormData({ ...formData, participantCode: value });
                      }}
                      style={{ textTransform: "uppercase" }}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      2. Please select the assigned version of the study. *
                    </label>
                    <select
                      value={formData.assignedMode}
                      onChange={(e) => {
                        setFormData({ ...formData, assignedMode: e.target.value });
                      }}
                      style={{ padding: "8px", fontSize: "14px", width: "100%" }}
                      required
                    >
                      <option value="">-- Select a condition --</option>
                      <option value={CONFIGS.MODE_1}>{CONFIGS.MODE_1}</option>
                      <option value={CONFIGS.MODE_2}>{CONFIGS.MODE_2}</option>
                      <option value={CONFIGS.MODE_3}>{CONFIGS.MODE_3}</option>
                      <option value={CONFIGS.MODE_4}>{CONFIGS.MODE_4}</option>
                      <option value={CONFIGS.MODE_5}>{CONFIGS.MODE_5}</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="page">
            {/* Demographics Section */}
            <div className="section">
              <div className="form-group">
                <label>1. Which age group do you belong to? *</label>
                <div className="radio-group">
                  {["18-24", "25-34", "35-44", "45-54", "55-64", "65+"].map(
                    (option) => (
                      <label key={option}>
                        <input
                          type="radio"
                          name="ageGroup"
                          value={option}
                          checked={formData.ageGroup === option}
                          onChange={handleChange}
                          required
                        />
                        {option}
                      </label>
                    )
                  )}
                </div>
                <p className="helper-text">You must be at least 18 years old to take part.</p>
              </div>

              <div className="form-group">
                <label>2. Which gender do you identify with? *</label>
                <div className="radio-group">
                  {["Male", "Female", "Non-binary", "Prefer not to say", "Other"].map(
                    (option) => (
                      <label key={option}>
                        <input
                          type="radio"
                          name="gender"
                          value={option}
                          checked={formData.gender === option}
                          onChange={handleChange}
                          required
                        />
                        {option}
                      </label>
                    )
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>3. What best describes your profession? *</label>
                <input
                  type="text"
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  placeholder="e.g., Student, Software developer, Teacher"
                  required
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="page">
            {/* ATI Scale Section */}
            <div className="section">
              <p className="scale-info">
                Please indicate how much you agree with each statement.
              </p>

              <table className="likert-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Completely disagree</th>
                    <th>Largely disagree</th>
                    <th>Slightly disagree</th>
                    <th>Slightly agree</th>
                    <th>Largely agree</th>
                    <th>Completely agree</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { text: "I like to engage with AI systems in greater detail.", field: "occupyDetail" },
                    { text: "I like testing the functions of new AI systems.", field: "testFunctions" },
                    { text: "I predominantly deal with AI systems because I have to.", field: "dealBecauseMust" },
                    { text: "When I have a new AI system in front of me, I try it out intensively.", field: "tryIntensively" },
                    { text: "I enjoy spending time becoming acquainted with a new AI system.", field: "enjoyAcquainting" },
                    { text: "It is enough for me that an AI system works; I don't care how or why.", field: "enoughWorks" },
                    { text: "I try to understand how an AI system exactly works.", field: "understandHow" },
                    { text: "It is enough for me to know the basic functions of an AI system.", field: "enoughBasic" },
                    { text: "I try to make full use of the capabilities of an AI system.", field: "fullCapabilities" },
                  ].map((question, index) => (
                    <tr key={index}>
                      <td>
                        {index + 1}. {question.text}
                      </td>
                      {[1, 2, 3, 4, 5, 6].map((value) => (
                        <td key={value}>
                          <input
                            type="radio"
                            name={`ati-${question.field}`}
                            checked={formData.ati[question.field] === value.toString()}
                            onChange={() => handleNestedChange("ati", question.field, value.toString())}
                            required
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="page">
            <div className="section">
              <p className="scale-info">
                Please choose the option that best describes your English level in each area.
              </p>
              <table className="likert-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Very limited (basic words/phrases)</th>
                    <th>Limited (simple everyday use)</th>
                    <th>Functional (routine tasks)</th>
                    <th>Independent (complex tasks)</th>
                    <th>Proficient (most contexts)</th>
                    <th>Near-native (no difficulty)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { text: "Listening", field: "listening" },
                    { text: "Reading", field: "reading" },
                    { text: "Speaking at length (speaking on your own)", field: "spokenInteraction" },
                    { text: "Conversation (talking with other people)", field: "spokenProduction" },
                    { text: "Writing", field: "writing" },
                  ].map((question, index) => (
                    <tr key={index}>
                      <td>
                        {index + 12}. {question.text}
                      </td>
                      {[1, 2, 3, 4, 5, 6].map((value) => (
                        <td key={value}>
                          <input
                            type="radio"
                            name={`english-${question.field}`}
                            checked={formData.englishProficiency[question.field] === value.toString()}
                            onChange={() => handleNestedChange("englishProficiency", question.field, value.toString())}
                            required
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="page">
            {/* TIPI Section */}
            <div className="section">
              <p className="scale-info">
                Here are a number of personality traits that may or may not apply
                to you. Please rate how much you agree that each pair of traits
                applies to you, even if one trait applies more strongly than the
                other. Tap the small “i” next to a word to see what it means.
              </p>
              <p className="scale-info" style={{ fontWeight: 600 }}>
                I see myself as:
              </p>

              <table className="likert-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Disagree strongly</th>
                    <th>Disagree moderately</th>
                    <th>Disagree a little</th>
                    <th>Neither agree nor disagree</th>
                    <th>Agree a little</th>
                    <th>Agree moderately</th>
                    <th>Agree strongly</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { text: <>{term("Extraverted")}, {term("enthusiastic")}.</>, field: "extraverted" },
                    { text: <>{term("Critical")}, {term("quarrelsome")}.</>, field: "critical" },
                    { text: <>{term("Dependable")}, {term("self-disciplined")}.</>, field: "dependable" },
                    { text: <>{term("Anxious")}, easily upset.</>, field: "anxious" },
                    { text: <>{term("Open to new experiences")}, {term("complex")}.</>, field: "openToExperiences" },
                    { text: <>{term("Reserved")}, quiet.</>, field: "reserved" },
                    { text: <>{term("Sympathetic")}, warm.</>, field: "sympathetic" },
                    { text: <>{term("Disorganised")}, careless.</>, field: "disorganised" },
                    { text: <>Calm, emotionally stable.</>, field: "calm" },
                    { text: <>{term("Conventional")}, {term("uncreative")}.</>, field: "conventional" },
                  ].map((question, index) => (
                    <tr key={index}>
                      <td>
                        {index + 1}. {question.text}
                      </td>
                      {[1, 2, 3, 4, 5, 6, 7].map((value) => (
                        <td key={value}>
                          <input
                            type="radio"
                            name={`tipi-${question.field}`}
                            checked={formData.tipi[question.field] === value.toString()}
                            onChange={() => handleNestedChange("tipi", question.field, value.toString())}
                            required
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const fillDebugData = () => {
    setFormData((prev) =>
      ({
        ...getDebugStartingQuestionnaireData({
          participantCode: isProlific ? prev.participantCode : "DEBUG001",
          assignedMode: isProlific ? prev.assignedMode : CONFIGS.MODE_1,
        }),
        consentAccepted: prev.consentAccepted,
        consentVersion: prev.consentVersion,
        consentTimestamp: prev.consentTimestamp,
      })
    );
  };

  return (
    <div className="root-container">
      <div className="app">
        <div className="form-container">
          <h1>{STEP_TITLES[currentStep]}</h1>

          {(DEBUG_MODE || showDebugControls) && !accessOnly && (
            <Button
              variant="outlined"
              color="warning"
              onClick={fillDebugData}
              sx={{ marginBottom: 2 }}
            >
              🔧 Debug: Fill All Fields
            </Button>
          )}

          {validationError && (
            <div className="validation-error">{validationError}</div>
          )}

          <form onSubmit={handleSubmission}>
            {renderStep()}

            <div className="navigation-buttons">
              {currentStep > firstStep && (
                <Button variant="outlined" onClick={prevStep} type="button">
                  Back
                </Button>
              )}
              {(accessOnly || currentStep < 5) && (
                <Button variant="contained" onClick={nextStep} type="button">
                  {accessOnly ? "Continue" : "Next"}
                </Button>
              )}
              {!accessOnly && currentStep === 5 && (
                <Button variant="contained" type="submit">
                  Start Study
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Questionnaire;
